import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ShareNetwork, PaperPlaneTilt, Sparkle, Megaphone, Trash, Plus, Quotes,
  FacebookLogo, InstagramLogo, LinkedinLogo, XLogo, YoutubeLogo, PinterestLogo, CheckCircle, Clock, Warning, PencilSimple,
  UploadSimple, Image as ImageIcon, X as XIcon, ArrowsClockwise, Pause, Play, Stop,
  ArrowClockwise, ArrowSquareOut, BookOpen, ShieldCheck, Sliders, Circle, Robot,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, API_ORIGIN, API_BASE } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLATFORM_ICON = { facebook: FacebookLogo, instagram: InstagramLogo, linkedin: LinkedinLogo, x: XLogo, pinterest: PinterestLogo, youtube: YoutubeLogo };
const FEATURE_LABEL = { text: "Text", image: "Images", video: "Video", article: "Articles", carousel: "Carousel", shorts: "Shorts", pin: "Pins", scheduling: "Scheduling", delete: "Delete", analytics: "Analytics", refresh_token: "Refresh", webhooks: "Webhooks" };
// Read-only informational references per platform (Social Control Center advanced panel).
const DEV_PORTAL = {
  facebook: "https://developers.facebook.com/apps/", instagram: "https://developers.facebook.com/apps/",
  linkedin: "https://www.linkedin.com/developers/apps", x: "https://developer.x.com/en/portal/dashboard",
  pinterest: "https://developers.pinterest.com/apps/", youtube: "https://console.cloud.google.com/apis/credentials",
};
const API_DOCS = {
  facebook: "https://developers.facebook.com/docs/graph-api/", instagram: "https://developers.facebook.com/docs/instagram-api/",
  linkedin: "https://learn.microsoft.com/linkedin/marketing/", x: "https://docs.x.com/x-api",
  pinterest: "https://developers.pinterest.com/docs/api/v5/", youtube: "https://developers.google.com/youtube/v3",
};
const PLATFORM_API_BASE = {
  facebook: "https://graph.facebook.com", instagram: "https://graph.facebook.com",
  linkedin: "https://api.linkedin.com", x: "https://api.x.com/2",
  pinterest: "https://api.pinterest.com/v5", youtube: "https://www.googleapis.com/youtube/v3",
};
const STATUS_STYLE = {
  draft: { variant: "secondary", Icon: PencilSimple },
  scheduled: { variant: "outline", Icon: Clock },
  published: { variant: "default", Icon: CheckCircle },
  partial: { variant: "outline", Icon: Warning },
  failed: { variant: "destructive", Icon: Warning },
  recurring: { variant: "outline", Icon: ArrowsClockwise },
  paused: { variant: "outline", Icon: Pause },
  completed: { variant: "secondary", Icon: CheckCircle },
};

function fmtTs(v) {
  if (!v) return "—";
  const ms = Number(v) < 1e12 ? Number(v) * 1000 : Number(v);
  const d = new Date(ms);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
const HEALTH_COLOR = { connected: "bg-emerald-500", healthy: "bg-emerald-500", token_expired: "bg-amber-500", rate_limited: "bg-amber-500", permission_missing: "bg-amber-500", disconnected: "bg-red-500" };
function HealthDot({ status }) {
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${HEALTH_COLOR[status] || "bg-muted-foreground"}`} />;
}
function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground/70">{label}</div>
      <div className="font-mono text-[10px] break-all text-foreground/80">{value || "—"}</div>
    </div>
  );
}

export default function Social() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [platforms, setPlatforms] = useState([]);
  const [connectorList, setConnectorList] = useState([]);
  const [connCreds, setConnCreds] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bases, setBases] = useState([]);
  const [health, setHealth] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [xWeb, setXWeb] = useState({ sessions: [], publishing_enabled: false });
  const [xWebOpen, setXWebOpen] = useState(false);
  const [xWebForm, setXWebForm] = useState({ handle: "", storage_state: "" });
  const [xWebBusy, setXWebBusy] = useState(false);
  const [fbWeb, setFbWeb] = useState({ sessions: [], publishing_enabled: false });
  const [fbWebOpen, setFbWebOpen] = useState(false);
  const [fbWebForm, setFbWebForm] = useState({ handle: "", storage_state: "" });
  const [fbWebBusy, setFbWebBusy] = useState(false);
  const [igWeb, setIgWeb] = useState({ sessions: [], publishing_enabled: false });
  const [igWebOpen, setIgWebOpen] = useState(false);
  const [igWebForm, setIgWebForm] = useState({ handle: "", storage_state: "" });
  const [igWebBusy, setIgWebBusy] = useState(false);

  const [newAcc, setNewAcc] = useState({ platform: "linkedin", handle: "" });
  const [metaApp, setMetaApp] = useState({ app_id: "", app_secret: "" });
  const [newCampaign, setNewCampaign] = useState({ name: "", objective: "" });
  const [gen, setGen] = useState({ topic: "", platform: "linkedin", kb_id: "", tone: "", provider: "openai" });
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState({ content: "", account_ids: [], campaign_id: "", scheduled_at: "", post_type: "text", media_ids: [], publishing_mode: "one_time", recurrence_interval: 1, recurrence_unit: "days", first_publish_at: "", end_at: "", max_runs: "" });
  const RESET_DRAFT = { content: "", account_ids: [], campaign_id: "", scheduled_at: "", post_type: "text", media_ids: [], publishing_mode: "one_time", recurrence_interval: 1, recurrence_unit: "days", first_publish_at: "", end_at: "", max_runs: "" };
  const [attachedMedia, setAttachedMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    api.get("/social/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
    api.get("/social/connectors").then((r) => setConnectorList(r.data)).catch(() => {});
    api.get("/social/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/social/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/social/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/social/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/knowledge/bases").then((r) => setBases(r.data)).catch(() => {});
    api.get("/social/x-web/status").then((r) => setXWeb(r.data)).catch(() => {});
    api.get("/social/fb-web/status").then((r) => setFbWeb(r.data)).catch(() => {});
    api.get("/social/ig-web/status").then((r) => setIgWeb(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("meta") === "connected") {
      toast.success(t("social.metaConnected", { count: p.get("linked") || 0 }));
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (p.get("meta") === "error") {
      toast.error(t("social.metaError"));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (p.get("connect") === "connected") {
      toast.success(`${p.get("platform") || "Account"} connected (${p.get("linked") || 0} account${p.get("linked") === "1" ? "" : "s"})`);
      window.history.replaceState({}, "", window.location.pathname);
      load();
    } else if (p.get("connect") === "error") {
      toast.error("Connection failed. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectPlatform = async (platform) => {
    const creds = connCreds[platform] || {};
    const body = creds.client_id && creds.client_secret
      ? { client_id: creds.client_id, client_secret: creds.client_secret } : {};
    try {
      const { data } = await api.post(`/social/${platform}/connect`, body);
      if (data.mode === "real" && data.authorize_url) { window.location.href = data.authorize_url; return; }
      toast.success(`${platform} connected (${data.count} account${data.count === 1 ? "" : "s"})`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const connectMeta = async () => {
    try {
      const { data } = await api.post("/social/meta/oauth/start", metaApp);
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  // --- X Web Publisher (SPRINT-110 Phase 2A): manual storage_state session import ---
  const importXWebSession = async () => {
    let parsed;
    try { parsed = JSON.parse(xWebForm.storage_state); }
    catch { toast.error("storage_state must be valid JSON"); return; }
    setXWebBusy(true);
    try {
      await api.post("/social/x-web/session", { handle: xWebForm.handle, storage_state: parsed });
      toast.success("Browser session stored (encrypted)");
      setXWebForm({ handle: "", storage_state: "" });
      const { data } = await api.get("/social/x-web/status");
      setXWeb(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setXWebBusy(false); }
  };

  const verifyXWebSession = async (id) => {
    setXWebBusy(true);
    try {
      const { data } = await api.post(`/social/x-web/session/${id}/verify`);
      const label = { verified: "Session verified — logged in", invalid: "Session invalid — not logged in", unreachable: "Could not reach x.com from server", no_session: "No stored session" }[data.status] || data.status;
      (data.status === "verified" ? toast.success : toast.warning)(label);
      const { data: st } = await api.get("/social/x-web/status");
      setXWeb(st);
    } catch (e) { toast.error(apiError(e)); }
    finally { setXWebBusy(false); }
  };

  const destroyXWebSession = async (id) => {
    setXWebBusy(true);
    try {
      await api.delete(`/social/x-web/session/${id}`);
      toast.success("Browser session destroyed");
      const { data } = await api.get("/social/x-web/status");
      setXWeb(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setXWebBusy(false); }
  };

  // --- Facebook Browser Publisher (SPRINT-112): manual storage_state session import ---
  const importFbWebSession = async () => {
    let parsed;
    try { parsed = JSON.parse(fbWebForm.storage_state); }
    catch { toast.error("storage_state must be valid JSON"); return; }
    setFbWebBusy(true);
    try {
      await api.post("/social/fb-web/session", { handle: fbWebForm.handle, storage_state: parsed });
      toast.success("Facebook browser session stored (encrypted)");
      setFbWebForm({ handle: "", storage_state: "" });
      setFbWebOpen(false);
      const { data } = await api.get("/social/fb-web/status");
      setFbWeb(data);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setFbWebBusy(false); }
  };

  const verifyFbWebSession = async (id) => {
    setFbWebBusy(true);
    try {
      const { data } = await api.post(`/social/fb-web/session/${id}/verify`);
      const label = { verified: "Session verified — logged in", invalid: "Session invalid — not logged in", unreachable: "Could not reach facebook.com from server", no_session: "No stored session" }[data.status] || data.status;
      (data.status === "verified" ? toast.success : toast.warning)(label);
      const { data: st } = await api.get("/social/fb-web/status");
      setFbWeb(st);
    } catch (e) { toast.error(apiError(e)); }
    finally { setFbWebBusy(false); }
  };

  const destroyFbWebSession = async (id) => {
    setFbWebBusy(true);
    try {
      await api.delete(`/social/fb-web/session/${id}`);
      toast.success("Facebook browser session destroyed");
      const { data } = await api.get("/social/fb-web/status");
      setFbWeb(data);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setFbWebBusy(false); }
  };

  // --- Instagram Browser Publisher (SPRINT-117): manual storage_state import (Story only) ---
  const importIgWebSession = async () => {
    let parsed;
    try { parsed = JSON.parse(igWebForm.storage_state); }
    catch { toast.error("storage_state must be valid JSON"); return; }
    setIgWebBusy(true);
    try {
      await api.post("/social/ig-web/session", { handle: igWebForm.handle, storage_state: parsed });
      toast.success("Instagram browser session stored (encrypted)");
      setIgWebForm({ handle: "", storage_state: "" });
      setIgWebOpen(false);
      const { data } = await api.get("/social/ig-web/status");
      setIgWeb(data);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setIgWebBusy(false); }
  };

  const verifyIgWebSession = async (id) => {
    setIgWebBusy(true);
    try {
      const { data } = await api.post(`/social/ig-web/session/${id}/verify`);
      const label = { verified: "Session verified — logged in", invalid: "Session invalid — not logged in", unreachable: "Could not reach instagram.com from server", no_session: "No stored session" }[data.status] || data.status;
      (data.status === "verified" ? toast.success : toast.warning)(label + (data.story_composer_detected ? " · Story composer detected" : ""));
      const { data: st } = await api.get("/social/ig-web/status");
      setIgWeb(st);
    } catch (e) { toast.error(apiError(e)); }
    finally { setIgWebBusy(false); }
  };

  const destroyIgWebSession = async (id) => {
    setIgWebBusy(true);
    try {
      await api.delete(`/social/ig-web/session/${id}`);
      toast.success("Instagram browser session destroyed");
      const { data } = await api.get("/social/ig-web/status");
      setIgWeb(data);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setIgWebBusy(false); }
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error(t("social.imageOnly")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: asset } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absUrl = `${API_ORIGIN}${asset.signed_url}`;
      const { data: sm } = await api.post("/social/media", { url: absUrl, media_type: "image", media_ref: asset.id, filename: asset.filename });
      setAttachedMedia((m) => [...m, { id: sm.id, url: absUrl, name: asset.filename }]);
      setDraft((d) => ({ ...d, media_ids: [...d.media_ids, sm.id] }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const removeMedia = (id) => {
    setAttachedMedia((m) => m.filter((x) => x.id !== id));
    setDraft((d) => ({ ...d, media_ids: d.media_ids.filter((x) => x !== id) }));
  };

  const publishToast = (status) => {
    if (status === "partial") toast.warning(t("social.partialSuccess"));
    else if (status === "failed") toast.error(t("social.publishFailed"));
    else toast.success(t("social.published"));
  };

  const connectAccount = async () => {
    try { await api.post("/social/accounts", newAcc); setNewAcc({ platform: "linkedin", handle: "" }); load(); toast.success(t("social.accountConnected")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const disconnectAccount = async (id) => {
    try { await api.delete(`/social/accounts/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const refreshAccount = async (id) => {
    try {
      const { data } = await api.post(`/social/accounts/${id}/refresh`);
      if (data.status === "unsupported") toast.info(data.reason || "This account cannot be refreshed");
      else toast.success("Account token refreshed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const createCampaign = async () => {
    try { await api.post("/social/campaigns", newCampaign); setNewCampaign({ name: "", objective: "" }); load(); toast.success(t("social.campaignCreated")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteCampaign = async (id) => {
    try { await api.delete(`/social/campaigns/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const generateContent = async () => {
    setGenerating(true); setGenerated(null);
    try {
      const { data } = await api.post("/social/content/generate", { ...gen, kb_id: gen.kb_id || null, tone: gen.tone || null });
      setGenerated(data);
      setDraft((d) => ({ ...d, content: data.content }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };
  const toggleAccount = (id) => {
    setDraft((d) => ({ ...d, account_ids: d.account_ids.includes(id) ? d.account_ids.filter((x) => x !== id) : [...d.account_ids, id] }));
  };
  const createPost = async (publish) => {
    try {
      const rec = draft.publishing_mode === "recurring";
      const body = { content: draft.content, account_ids: draft.account_ids,
        campaign_id: draft.campaign_id || null, post_type: draft.post_type, media_ids: draft.media_ids,
        publishing_mode: draft.publishing_mode,
        scheduled_at: rec ? null : (draft.scheduled_at || null),
        recurrence_interval: rec ? Number(draft.recurrence_interval) || 1 : null,
        recurrence_unit: rec ? draft.recurrence_unit : null,
        first_publish_at: rec ? (draft.first_publish_at || null) : null,
        end_at: rec ? (draft.end_at || null) : null,
        max_runs: rec && draft.max_runs ? Number(draft.max_runs) : null };
      const { data } = await api.post("/social/posts", body);
      if (publish && !rec) {
        const { data: res } = await api.post(`/social/posts/${data.id}/publish`);
        publishToast(res.status);
      } else if (rec) {
        toast.success(t("social.recurringCreated"));
      } else {
        toast.success(t("social.postSaved"));
      }
      setDraft(RESET_DRAFT);
      setAttachedMedia([]);
      setGenerated(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const publishPost = async (id) => {
    try { const { data } = await api.post(`/social/posts/${id}/publish`); load(); publishToast(data.status); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deletePost = async (id) => {
    try { await api.delete(`/social/posts/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const recurrenceControl = async (id, action) => {
    try {
      await api.post(`/social/posts/${id}/recurrence/${action}`);
      toast.success(t(`social.${action}Done`));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const accName = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.platform}/${a.handle}` : id; };

  // Dedupe connectors by platform, preferring the REAL one (simulated===false).
  // Simulated connectors stay in the registry for compatibility/tests but are
  // hidden from the end user when a real connector exists for that platform.
  const uniquePlatforms = Object.values(platforms.reduce((acc, p) => {
    const prev = acc[p.platform];
    if (!prev || (prev.simulated && !p.simulated)) acc[p.platform] = p;
    return acc;
  }, {}));
  const simulatedOnly = uniquePlatforms.filter((p) => p.simulated);
  // X Web Publisher (Phase 1 scaffold) — rendered as a SECOND method row inside the X card,
  // never as its own card. Fully separate connector; shares no auth state with the X API.
  const xWebPublisher = connectorList.find((c) => c.connector_id === "social.x_web");
  const fbWebPublisher = connectorList.find((c) => c.connector_id === "social.meta_facebook_web");
  const igWebPublisher = connectorList.find((c) => c.connector_id === "social.meta_instagram_web");
  const metaRedirectUri = `${API_BASE}/social/meta/oauth/callback`;

  return (
    <div data-testid="social-page">
      <PageHeader title={t("social.title")} subtitle={t("social.subtitle")} />
      <div className="mb-4 text-[11px] font-mono text-muted-foreground border border-dashed border-border rounded-sm px-3 py-2 inline-block" data-testid="social-connector-note">
        ⚡ {t("social.connectorStatusNote")}
      </div>

      <Tabs defaultValue="content">
        <TabsList className="rounded-sm">
          <TabsTrigger value="content" className="rounded-sm" data-testid="social-tab-content"><Sparkle size={15} className="mr-1" />{t("social.tabContent")}</TabsTrigger>
          <TabsTrigger value="posts" className="rounded-sm" data-testid="social-tab-posts"><PaperPlaneTilt size={15} className="mr-1" />{t("social.tabPosts")}</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-sm" data-testid="social-tab-campaigns"><Megaphone size={15} className="mr-1" />{t("social.tabCampaigns")}</TabsTrigger>
          <TabsTrigger value="accounts" className="rounded-sm" data-testid="social-tab-accounts"><ShareNetwork size={15} className="mr-1" />{t("social.tabAccounts")}</TabsTrigger>
        </TabsList>

        {/* CONTENT STUDIO */}
        <TabsContent value="content" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Generator */}
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <div className="overline text-muted-foreground">{t("social.aiGenerator")}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("social.platform")}</Label>
                  <Select value={gen.platform} onValueChange={(v) => setGen({ ...gen, platform: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="gen-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{uniquePlatforms.map((p) => <SelectItem key={p.platform} value={p.platform}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("social.knowledgeBase")}</Label>
                  <Select value={gen.kb_id} onValueChange={(v) => setGen({ ...gen, kb_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="gen-kb-select"><SelectValue placeholder={t("social.kbPlaceholder")} /></SelectTrigger>
                    <SelectContent>{bases.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("social.topic")}</Label>
                <Textarea value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} className="rounded-sm min-h-20" placeholder={t("social.topicPlaceholder")} data-testid="gen-topic-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("social.tone")}</Label>
                <Input value={gen.tone} onChange={(e) => setGen({ ...gen, tone: e.target.value })} className="rounded-sm" placeholder={t("social.tonePlaceholder")} data-testid="gen-tone-input" />
              </div>
              <Button onClick={generateContent} disabled={generating || !gen.topic.trim()} className="rounded-sm" data-testid="gen-run-button">
                <Sparkle size={16} className="mr-2" />{generating ? t("social.generating") : t("social.generate")}
              </Button>
              {generated && (
                <div className="border-t border-border pt-4 space-y-3" data-testid="gen-result">
                  <div className="text-sm leading-relaxed whitespace-pre-wrap bg-secondary/50 p-3 rounded-sm">{generated.content}</div>
                  {generated.citations?.length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center">
                      <Quotes size={14} className="text-muted-foreground" />
                      {generated.citations.map((c, i) => <Badge key={i} variant="outline" className="rounded-sm font-mono text-[10px]">{c.source}</Badge>)}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground font-mono">{generated.provider}/{generated.model} · {t("social.copiedToDraft")}</div>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <div className="overline text-muted-foreground">{t("social.composer")}</div>
              <div className="space-y-2">
                <Label>{t("social.content")}</Label>
                <Textarea value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} className="rounded-sm min-h-28" placeholder={t("social.contentPlaceholder")} data-testid="post-content-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("social.targetAccounts")}</Label>
                <div className="flex flex-wrap gap-2">
                  {accounts.length === 0 && <span className="text-xs text-muted-foreground">{t("social.noAccountsHint")}</span>}
                  {accounts.map((a) => {
                    const Icon = PLATFORM_ICON[a.platform] || ShareNetwork;
                    const on = draft.account_ids.includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAccount(a.id)} data-testid={`draft-account-${a.id}`}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                        <Icon size={14} weight="bold" />{a.handle}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("social.postType")}</Label>
                  <Select value={draft.post_type} onValueChange={(v) => setDraft({ ...draft, post_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="draft-posttype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">{t("social.ptText")}</SelectItem>
                      <SelectItem value="image">{t("social.ptImage")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("social.campaign")}</Label>
                  <Select value={draft.campaign_id} onValueChange={(v) => setDraft({ ...draft, campaign_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="draft-campaign-select"><SelectValue placeholder={t("common.none")} /></SelectTrigger>
                    <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>{t("social.publishingMode")}</Label>
                  <Select value={draft.publishing_mode} onValueChange={(v) => setDraft({ ...draft, publishing_mode: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="draft-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">{t("social.modeOneTime")}</SelectItem>
                      <SelectItem value="recurring">{t("social.modeRecurring")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {draft.publishing_mode === "one_time" ? (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>{t("social.schedule")}</Label>
                    <Input type="datetime-local" value={draft.scheduled_at ? draft.scheduled_at.slice(0, 16) : ""} onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-sm" data-testid="draft-schedule-input" />
                  </div>
                ) : (
                  <div className="sm:col-span-2 space-y-3 border border-primary/30 rounded-sm p-3" data-testid="recurrence-config">
                    <div className="flex items-end gap-2 flex-wrap">
                      <div className="space-y-1"><Label className="text-xs">{t("social.repeatEvery")}</Label>
                        <Input type="number" min="1" value={draft.recurrence_interval} onChange={(e) => setDraft({ ...draft, recurrence_interval: e.target.value })} className="rounded-sm w-24" data-testid="recurrence-interval-input" /></div>
                      <div className="space-y-1 min-w-[130px]"><Label className="text-xs">&nbsp;</Label>
                        <Select value={draft.recurrence_unit} onValueChange={(v) => setDraft({ ...draft, recurrence_unit: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="recurrence-unit-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["minutes", "hours", "days", "weeks", "months", "years"].map((u) => <SelectItem key={u} value={u}>{t(`social.unit_${u}`)}</SelectItem>)}
                          </SelectContent>
                        </Select></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">{t("social.startAt")}</Label>
                        <Input type="datetime-local" value={draft.first_publish_at ? draft.first_publish_at.slice(0, 16) : ""} onChange={(e) => setDraft({ ...draft, first_publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-sm" data-testid="recurrence-start-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">{t("social.endAt")}</Label>
                        <Input type="datetime-local" value={draft.end_at ? draft.end_at.slice(0, 16) : ""} onChange={(e) => setDraft({ ...draft, end_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-sm" data-testid="recurrence-end-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">{t("social.maxRuns")}</Label>
                        <Input type="number" min="1" placeholder={t("social.infinite")} value={draft.max_runs} onChange={(e) => setDraft({ ...draft, max_runs: e.target.value })} className="rounded-sm" data-testid="recurrence-maxruns-input" /></div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t("social.recurrenceHint")}</p>
                  </div>
                )}
              </div>
              {draft.post_type === "image" && (
                <div className="space-y-2" data-testid="image-uploader">
                  <Label>{t("social.imageUpload")}</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) uploadImage(e.dataTransfer.files[0]); }}
                    className="border-2 border-dashed border-border rounded-sm p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/50 transition-colors"
                    data-testid="image-dropzone"
                  >
                    <UploadSimple size={22} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground text-center">{uploading ? t("social.uploading") : t("social.dropImage")}</span>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" data-testid="image-file-input"
                      onChange={(e) => { if (e.target.files?.[0]) uploadImage(e.target.files[0]); e.target.value = ""; }} />
                  </div>
                  {attachedMedia.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="media-previews">
                      {attachedMedia.map((m) => (
                        <div key={m.id} className="relative w-20 h-20 rounded-sm overflow-hidden border border-border group" data-testid={`media-preview-${m.id}`}>
                          <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
                          <button onClick={() => removeMedia(m.id)} data-testid={`remove-media-${m.id}`}
                            className="absolute top-0.5 right-0.5 bg-background/80 rounded-sm p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <XIcon size={12} className="text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachedMedia.length === 0 && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ImageIcon size={12} />{t("social.imageRequiredHint")}</p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {draft.publishing_mode === "one_time" && (
                  <Button variant="outline" onClick={() => createPost(false)} disabled={!draft.content.trim() || draft.account_ids.length === 0} className="rounded-sm" data-testid="save-post-button">
                    {draft.scheduled_at ? t("social.schedulePost") : t("social.saveDraft")}
                  </Button>
                )}
                <Button onClick={() => createPost(true)} disabled={!draft.content.trim() || draft.account_ids.length === 0 || (draft.post_type === "image" && draft.media_ids.length === 0)} className="rounded-sm" data-testid="publish-post-button">
                  <PaperPlaneTilt size={16} className="mr-2" />
                  {draft.publishing_mode === "recurring" ? t("social.createRecurring") : t("social.publishNow")}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* POSTS */}
        <TabsContent value="posts" className="mt-4">
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="posts-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("social.content")}</TableHead><TableHead>{t("social.accounts")}</TableHead>
                <TableHead>{t("common.status")}</TableHead><TableHead>{t("social.schedule")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {posts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">{t("social.noPosts")}</TableCell></TableRow>}
                {posts.map((p) => {
                  const st = STATUS_STYLE[p.status] || STATUS_STYLE.draft;
                  return (
                    <TableRow key={p.id} data-testid={`post-row-${p.id}`}>
                      <TableCell className="text-xs max-w-xs truncate">{p.content}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {(p.results && p.results.length) ? (
                          <div className="space-y-1" data-testid={`post-results-${p.id}`}>
                            {p.results.map((r, i) => (
                              <div key={i} className="flex items-center gap-1.5" data-testid={`post-result-${p.id}-${r.platform}`}>
                                {r.ok ? <CheckCircle size={12} weight="fill" className="text-green-600 shrink-0" /> : <Warning size={12} weight="fill" className="text-destructive shrink-0" />}
                                <span>{r.platform}{r.handle ? `/${r.handle}` : ""}</span>
                                {!r.ok && r.error && <span className="text-destructive truncate max-w-[220px]" title={r.error}>— {String(r.error).split("\n")[0].replace(/ for url.*/i, "")}</span>}
                              </div>
                            ))}
                          </div>
                        ) : (p.account_ids || []).map(accName).join(", ")}
                      </TableCell>
                      <TableCell><Badge variant={st.variant} className={`rounded-sm text-[10px] gap-1 ${p.status === "partial" ? "border-amber-500 text-amber-600" : ""} ${p.status === "recurring" ? "border-primary text-primary" : ""} ${p.status === "paused" ? "border-amber-500 text-amber-600" : ""}`} data-testid={`post-status-${p.id}`}><st.Icon size={11} />{["partial", "recurring", "completed", "paused"].includes(p.status) ? t(`social.${p.status}`) : p.status}</Badge></TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {p.publishing_mode === "recurring" ? (
                          <div className="space-y-0.5" data-testid={`recur-info-${p.id}`}>
                            <div className="text-foreground">{t("social.freqEvery")} {p.recurrence_interval} {t(`social.unit_${p.recurrence_unit}`)}</div>
                            <div>{t("social.nextRun")}: {p.next_publish_at ? new Date(p.next_publish_at).toLocaleString() : "—"}</div>
                            <div>{t("social.lastRun")}: {p.last_publish_at ? new Date(p.last_publish_at).toLocaleString() : "—"}</div>
                            <div>{t("social.runs")}: {p.run_count || 0}{p.max_runs ? `/${p.max_runs}` : ""}</div>
                          </div>
                        ) : (p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : "—")}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {p.publishing_mode !== "recurring" && !["published", "recurring", "completed"].includes(p.status) && (
                          <Button size="sm" variant="ghost" onClick={() => publishPost(p.id)} data-testid={`publish-${p.id}`}><PaperPlaneTilt size={14} /></Button>
                        )}
                        {p.publishing_mode === "recurring" && p.status === "recurring" && (
                          <Button size="sm" variant="ghost" onClick={() => recurrenceControl(p.id, "pause")} title={t("social.pause")} data-testid={`pause-post-${p.id}`}><Pause size={14} /></Button>
                        )}
                        {p.publishing_mode === "recurring" && p.status === "paused" && (
                          <Button size="sm" variant="ghost" onClick={() => recurrenceControl(p.id, "resume")} className="text-primary" title={t("social.resume")} data-testid={`resume-post-${p.id}`}><Play size={14} /></Button>
                        )}
                        {p.publishing_mode === "recurring" && ["recurring", "paused"].includes(p.status) && (
                          <Button size="sm" variant="ghost" onClick={() => recurrenceControl(p.id, "stop")} title={t("social.stop")} data-testid={`stop-post-${p.id}`}><Stop size={14} /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deletePost(p.id)} className="text-destructive" data-testid={`delete-post-${p.id}`}><Trash size={14} /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* CAMPAIGNS */}
        <TabsContent value="campaigns" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={newCampaign.name} onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} className="rounded-sm" data-testid="campaign-name-input" /></div>
              <div className="space-y-2"><Label>{t("social.objective")}</Label><Input value={newCampaign.objective} onChange={(e) => setNewCampaign({ ...newCampaign, objective: e.target.value })} className="rounded-sm" data-testid="campaign-objective-input" /></div>
              <Button onClick={createCampaign} disabled={!newCampaign.name.trim()} className="rounded-sm" data-testid="create-campaign-button"><Plus size={16} className="mr-1" />{t("common.create")}</Button>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="campaigns-table">
            <Table>
              <TableHeader><TableRow><TableHead>{t("common.name")}</TableHead><TableHead>{t("social.objective")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("social.posts")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {campaigns.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">{t("social.noCampaigns")}</TableCell></TableRow>}
                {campaigns.map((c) => (
                  <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <TableCell className="font-medium text-sm">{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.objective || "—"}</TableCell>
                    <TableCell><Badge variant="secondary" className="rounded-sm text-[10px]">{c.status}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{c.post_count}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => deleteCampaign(c.id)} className="text-destructive" data-testid={`delete-campaign-${c.id}`}><Trash size={14} /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ACCOUNTS */}
        <TabsContent value="accounts" className="mt-4 space-y-4">
          {/* Real Meta OAuth connect (Facebook + Instagram) */}
          <div className="bg-card border border-primary/30 rounded-sm p-6 space-y-4" data-testid="meta-connect-card">
            <div className="flex items-center gap-2">
              <FacebookLogo size={20} weight="bold" className="text-primary" />
              <InstagramLogo size={20} weight="bold" className="text-primary" />
              <span className="overline text-muted-foreground">{t("social.connectMeta")}</span>
              <Badge variant="default" className="rounded-sm text-[10px] ml-1">{t("social.realBadge")}</Badge>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="meta-oauth-hint">{t("social.metaHint", { uri: metaRedirectUri })}</p>
            <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>{t("social.metaAppId")}</Label><Input value={metaApp.app_id} onChange={(e) => setMetaApp({ ...metaApp, app_id: e.target.value })} className="rounded-sm font-mono text-xs" placeholder="App ID" data-testid="meta-app-id-input" /></div>
              <div className="space-y-2"><Label>{t("social.metaAppSecret")}</Label><Input type="password" value={metaApp.app_secret} onChange={(e) => setMetaApp({ ...metaApp, app_secret: e.target.value })} className="rounded-sm font-mono text-xs" placeholder="App Secret" data-testid="meta-app-secret-input" /></div>
              <Button onClick={connectMeta} disabled={!metaApp.app_id.trim() || !metaApp.app_secret.trim()} className="rounded-sm" data-testid="connect-meta-button"><FacebookLogo size={16} className="mr-1" weight="bold" />{t("social.connectMetaBtn")}</Button>
            </div>
          </div>

          {/* Social Control Center — enterprise management console per connector */}
          <div className="space-y-3" data-testid="social-control-center">
            <div className="flex items-center justify-between">
              <span className="overline text-muted-foreground">Connected platforms</span>
              <Button size="sm" variant="ghost" onClick={load} data-testid="control-center-refresh"><ArrowClockwise size={14} className="mr-1" />Refresh</Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              {connectorList.filter((c) => (c.method || "api") !== "browser").map((c) => {
                const Icon = PLATFORM_ICON[c.platform] || ShareNetwork;
                const isMeta = c.platform === "facebook" || c.platform === "instagram";
                const accs = accounts.filter((a) =>
                  (a.connector_id === "social.x_web" || a.connector_id === "social.meta_facebook_web") ? false
                  : (a.connector_id ? a.connector_id === c.connector_id : a.platform === c.platform));
                const feats = Object.entries(c.feature_matrix || {}).filter(([, v]) => v).map(([k]) => FEATURE_LABEL[k] || k);
                const creds = connCreds[c.platform] || {};
                const prod = c.has_credentials;
                const open = expanded === c.platform;
                const oauth = c.oauth || {};
                return (
                  <div key={c.platform} className="bg-card border border-border rounded-sm p-5 space-y-4" data-testid={`connector-card-${c.platform}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center"><Icon size={22} weight="bold" className="text-primary" /></div>
                        <div>
                          <div className="font-heading font-bold text-sm flex items-center gap-1.5">{c.name}
                            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground font-normal"><ShieldCheck size={11} />Official API</span>
                          </div>
                          <div className="text-[11px] font-mono text-muted-foreground">{c.connector_id} · v{c.version}</div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <Badge variant={c.connected ? "default" : "secondary"} className="rounded-sm text-[10px]" data-testid={`connector-status-${c.platform}`}>{c.connected ? <><CheckCircle size={11} className="mr-1" />Connected</> : "Not connected"}</Badge>
                        <Badge variant={prod ? "default" : "outline"} className="rounded-sm text-[10px]">{prod ? "Production" : "Demo"}</Badge>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">{feats.map((f) => <Badge key={f} variant="outline" className="rounded-sm text-[10px]">{f}</Badge>)}</div>

                    {accs.length > 0 ? (
                      <div className="space-y-2">
                        {accs.map((a) => {
                          const h = health.find((x) => x.account_id === a.id) || {};
                          const status = h.status || a.status;
                          return (
                            <div key={a.id} className="flex items-center justify-between rounded-sm border border-border/60 bg-muted/20 px-3 py-2" data-testid={`account-card-${a.id}`}>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[11px] font-bold text-primary shrink-0">{(a.handle || a.username || "?").replace(/^@/, "").slice(0, 2).toUpperCase()}</div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{a.handle || a.username || "account"}</div>
                                  <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">
                                    <HealthDot status={status} /> {status}{a.token_expires_at ? ` · exp ${fmtTs(a.token_expires_at)}` : ""}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button size="icon" variant="ghost" title="Refresh account" onClick={() => refreshAccount(a.id)} data-testid={`refresh-${a.id}`}><ArrowClockwise size={14} /></Button>
                                <Button size="icon" variant="ghost" title="Disconnect" className="text-destructive" onClick={() => disconnectAccount(a.id)} data-testid={`disconnect-${a.id}`}><Trash size={14} /></Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : <div className="text-[11px] text-muted-foreground">No connected accounts.</div>}

                    <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
                      {isMeta
                        ? <span className="text-[11px] text-muted-foreground">Connect via the Meta panel above.</span>
                        : <Button size="sm" onClick={() => connectPlatform(c.platform)} className="rounded-sm" data-testid={`connect-${c.platform}-button`}><Plus size={14} className="mr-1" />{c.connected ? "Reconnect" : "Connect"}</Button>}
                      <a href={DEV_PORTAL[c.platform]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`portal-${c.platform}`}><ArrowSquareOut size={13} />Developer Portal</a>
                      <a href={API_DOCS[c.platform]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`docs-${c.platform}`}><BookOpen size={13} />API Docs</a>
                      <button onClick={() => setExpanded(open ? null : c.platform)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground ml-auto" data-testid={`advanced-${c.platform}`}><Sliders size={13} />{open ? "Hide" : "Advanced"}</button>
                    </div>

                    {c.platform === "x" && xWebPublisher && (
                      <div className="rounded-sm border border-dashed border-border/70 bg-muted/10 p-3 space-y-2" data-testid="x-web-publisher-card">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center"><Robot size={16} className="text-muted-foreground" /></div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium flex items-center gap-1.5">Browser Publisher
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground font-normal"><Robot size={11} />Browser Automation</span>
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground">{xWebPublisher.connector_id} · separate from Official API</div>
                            </div>
                          </div>
                          <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="x-web-publisher-status">{xWeb.publishing_enabled ? "Publishing ON" : "Publishing OFF"}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Server-side browser publishing for X (text). Independent connection — does not share the Official API token or session. {xWeb.publishing_enabled ? "Publishing is enabled: posts route through the browser session." : "Publishing is disabled: import a session to validate the browser worker."}</p>

                        {(xWeb.sessions || []).map((s) => {
                          const HEALTH = { healthy: { label: "Healthy", cls: "text-emerald-600" }, expired: { label: "Expired", cls: "text-amber-600" }, unreachable: { label: "Unreachable", cls: "text-muted-foreground" }, no_session: { label: "No session", cls: "text-muted-foreground" } };
                          const hstate = s.session_present ? (s.session_health || "unknown") : "no_session";
                          const h = HEALTH[hstate] || { label: hstate, cls: "text-muted-foreground" };
                          return (
                          <div key={s.id} className="flex items-center justify-between gap-2 rounded-sm border border-border/60 bg-background/40 px-2.5 py-1.5" data-testid={`x-web-session-${s.handle}`}>
                            <div className="min-w-0">
                              <div className="text-[11px] font-medium truncate flex items-center gap-1.5">@{s.handle}
                                <span className={`inline-flex items-center gap-1 ${h.cls}`} data-testid={`x-web-health-${s.handle}`}><Circle size={7} weight="fill" />{h.label}</span>
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {s.session_present ? `${s.session_meta?.cookie_count ?? 0} cookies · encrypted` : "no session"}
                                {s.last_verified_at ? ` · verified ${fmtTs(Date.parse(s.last_verified_at) / 1000)}` : ""}
                                {s.last_published_at ? ` · published ${fmtTs(Date.parse(s.last_published_at) / 1000)}` : ""}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button size="sm" variant="outline" disabled={xWebBusy || !s.session_present} onClick={() => verifyXWebSession(s.id)} className="rounded-sm h-7 text-[10px]" data-testid={`x-web-verify-${s.handle}`}><ShieldCheck size={12} className="mr-1" />Verify</Button>
                              <Button size="sm" variant="ghost" disabled={xWebBusy} onClick={() => destroyXWebSession(s.id)} className="rounded-sm h-7 text-[10px] text-red-500 hover:text-red-600" data-testid={`x-web-destroy-${s.handle}`}><Trash size={12} /></Button>
                            </div>
                          </div>
                          );
                        })}

                        {xWebOpen ? (
                          <div className="space-y-2 rounded-sm border border-border/60 bg-background/40 p-2.5" data-testid="x-web-import-form">
                            <Input value={xWebForm.handle} onChange={(e) => setXWebForm((f) => ({ ...f, handle: e.target.value }))} placeholder="X handle (e.g. growthai)" className="rounded-sm text-xs h-8" data-testid="x-web-handle-input" />
                            <Textarea value={xWebForm.storage_state} onChange={(e) => setXWebForm((f) => ({ ...f, storage_state: e.target.value }))} placeholder='Paste Playwright storage_state JSON: {"cookies":[...],"origins":[...]} — never include a password' rows={4} className="rounded-sm text-[10px] font-mono" data-testid="x-web-storage-state-input" />
                            <p className="text-[10px] text-muted-foreground">Log into X in your own browser, export a Playwright <code>storage_state</code> (cookies only), and paste it here. It is validated, encrypted (AES-256-GCM) and never logged.</p>
                            <div className="flex items-center gap-2">
                              <Button size="sm" disabled={xWebBusy || !xWebForm.handle || !xWebForm.storage_state} onClick={importXWebSession} className="rounded-sm h-7 text-[10px]" data-testid="x-web-import-button"><UploadSimple size={12} className="mr-1" />Import session</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setXWebOpen(false); setXWebForm({ handle: "", storage_state: "" }); }} className="rounded-sm h-7 text-[10px]" data-testid="x-web-cancel-button">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setXWebOpen(true)} className="rounded-sm" data-testid="x-web-configure-button"><Plus size={14} className="mr-1" />Import browser session</Button>
                        )}
                      </div>
                    )}

                    {c.platform === "facebook" && fbWebPublisher && (
                      <div className="rounded-sm border border-dashed border-border/70 bg-muted/10 p-3 space-y-2" data-testid="fb-web-publisher-card">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center"><Robot size={16} className="text-muted-foreground" /></div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium flex items-center gap-1.5">Browser Publisher
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground font-normal"><Robot size={11} />Browser Automation</span>
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground">{fbWebPublisher.connector_id} · separate from Official API</div>
                            </div>
                          </div>
                          <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="fb-web-publisher-status">{fbWeb.publishing_enabled ? "Publishing ON" : "Publishing OFF"}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Server-side browser publishing for Facebook using an imported browser session. Independent from the Official API — a Facebook Profile via browser can post alongside a Facebook Page via Official API in the same campaign.</p>

                        {(fbWeb.sessions || []).map((s) => {
                          const HEALTH = { healthy: { label: "Healthy", cls: "text-emerald-600" }, expired: { label: "Session expired", cls: "text-amber-600" }, unreachable: { label: "Unreachable", cls: "text-muted-foreground" }, no_session: { label: "Not connected", cls: "text-muted-foreground" } };
                          const hstate = s.session_present ? (s.session_health || "connected") : "no_session";
                          const h = HEALTH[hstate] || { label: hstate === "connected" ? "Connected" : hstate, cls: "text-emerald-600" };
                          return (
                          <div key={s.id} className="flex items-center justify-between gap-2 rounded-sm border border-border/60 bg-background/40 px-2.5 py-1.5" data-testid={`fb-web-session-${s.handle}`}>
                            <div className="min-w-0">
                              <div className="text-[11px] font-medium truncate flex items-center gap-1.5">{s.handle}
                                <span className={`inline-flex items-center gap-1 ${h.cls}`} data-testid={`fb-web-health-${s.handle}`}><Circle size={7} weight="fill" />{h.label}</span>
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {s.session_present ? `${s.session_meta?.cookie_count ?? 0} cookies · encrypted` : "no session"}
                                {s.last_verified_at ? ` · verified ${fmtTs(Date.parse(s.last_verified_at) / 1000)}` : ""}
                                {s.last_published_at ? ` · published ${fmtTs(Date.parse(s.last_published_at) / 1000)}` : ""}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button size="sm" variant="outline" disabled={fbWebBusy || !s.session_present} onClick={() => verifyFbWebSession(s.id)} className="rounded-sm h-7 text-[10px]" data-testid={`fb-web-verify-${s.handle}`}><ShieldCheck size={12} className="mr-1" />Verify</Button>
                              <Button size="sm" variant="ghost" disabled={fbWebBusy} onClick={() => destroyFbWebSession(s.id)} className="rounded-sm h-7 text-[10px] text-red-500 hover:text-red-600" data-testid={`fb-web-destroy-${s.handle}`}><Trash size={12} /></Button>
                            </div>
                          </div>
                          );
                        })}

                        {fbWebOpen ? (
                          <div className="space-y-2 rounded-sm border border-border/60 bg-background/40 p-2.5" data-testid="fb-web-import-form">
                            <Input value={fbWebForm.handle} onChange={(e) => setFbWebForm((f) => ({ ...f, handle: e.target.value }))} placeholder="Facebook profile name (e.g. myprofile)" className="rounded-sm text-xs h-8" data-testid="fb-web-handle-input" />
                            <Textarea value={fbWebForm.storage_state} onChange={(e) => setFbWebForm((f) => ({ ...f, storage_state: e.target.value }))} placeholder='Paste Playwright storage_state JSON: {"cookies":[...],"origins":[...]} — never include a password' rows={4} className="rounded-sm text-[10px] font-mono" data-testid="fb-web-storage-state-input" />
                            <p className="text-[10px] text-muted-foreground">Run <code>scripts/facebook_capture_session.py</code>, log into Facebook manually, then paste the exported <code>storage_state</code> (cookies only). Validated, encrypted (AES-256-GCM) and never logged.</p>
                            <div className="flex items-center gap-2">
                              <Button size="sm" disabled={fbWebBusy || !fbWebForm.handle || !fbWebForm.storage_state} onClick={importFbWebSession} className="rounded-sm h-7 text-[10px]" data-testid="fb-web-import-button"><UploadSimple size={12} className="mr-1" />Import session</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setFbWebOpen(false); setFbWebForm({ handle: "", storage_state: "" }); }} className="rounded-sm h-7 text-[10px]" data-testid="fb-web-cancel-button">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setFbWebOpen(true)} className="rounded-sm" data-testid="fb-web-configure-button"><Plus size={14} className="mr-1" />Import browser session</Button>
                        )}
                      </div>
                    )}

                    {c.platform === "instagram" && igWebPublisher && (
                      <div className="rounded-sm border border-dashed border-border/70 bg-muted/10 p-3 space-y-2" data-testid="ig-web-publisher-card">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center"><Robot size={16} className="text-muted-foreground" /></div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium flex items-center gap-1.5">Browser Publisher
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground font-normal"><Robot size={11} />Story only · Browser Automation</span>
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground">{igWebPublisher.connector_id} · separate from Official API</div>
                            </div>
                          </div>
                          <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="ig-web-publisher-status">{igWeb.publishing_enabled ? "Publishing ON" : "Publishing OFF"}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">API-first: normal Instagram Feed &amp; carousel keep using the Official Graph API above. This browser publisher only adds Instagram <strong>Stories</strong> (which the Graph API can't publish for this account). Independent from the Official API — no shared token or session.</p>

                        {(igWeb.sessions || []).map((s) => {
                          const HEALTH = { healthy: { label: "Healthy", cls: "text-emerald-600" }, expired: { label: "Session expired", cls: "text-amber-600" }, unreachable: { label: "Unreachable", cls: "text-muted-foreground" }, no_session: { label: "Not connected", cls: "text-muted-foreground" } };
                          const hstate = s.session_present ? (s.session_health || "connected") : "no_session";
                          const h = HEALTH[hstate] || { label: hstate === "connected" ? "Connected" : hstate, cls: "text-emerald-600" };
                          return (
                          <div key={s.id} className="flex items-center justify-between gap-2 rounded-sm border border-border/60 bg-background/40 px-2.5 py-1.5" data-testid={`ig-web-session-${s.handle}`}>
                            <div className="min-w-0">
                              <div className="text-[11px] font-medium truncate flex items-center gap-1.5">@{s.handle}
                                <span className={`inline-flex items-center gap-1 ${h.cls}`} data-testid={`ig-web-health-${s.handle}`}><Circle size={7} weight="fill" />{h.label}</span>
                                {s.story_capability ? <span className="text-[10px] text-emerald-600" data-testid={`ig-web-story-cap-${s.handle}`}>· Story composer ✓</span> : null}
                              </div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {s.session_present ? `${s.session_meta?.cookie_count ?? 0} cookies · encrypted` : "no session"}
                                {s.last_verified_at ? ` · verified ${fmtTs(Date.parse(s.last_verified_at) / 1000)}` : ""}
                                {s.last_published_at ? ` · published ${fmtTs(Date.parse(s.last_published_at) / 1000)}` : ""}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button size="sm" variant="outline" disabled={igWebBusy || !s.session_present} onClick={() => verifyIgWebSession(s.id)} className="rounded-sm h-7 text-[10px]" data-testid={`ig-web-verify-${s.handle}`}><ShieldCheck size={12} className="mr-1" />Verify</Button>
                              <Button size="sm" variant="ghost" disabled={igWebBusy} onClick={() => destroyIgWebSession(s.id)} className="rounded-sm h-7 text-[10px] text-red-500 hover:text-red-600" data-testid={`ig-web-destroy-${s.handle}`}><Trash size={12} /></Button>
                            </div>
                          </div>
                          );
                        })}

                        {igWebOpen ? (
                          <div className="space-y-2 rounded-sm border border-border/60 bg-background/40 p-2.5" data-testid="ig-web-import-form">
                            <Input value={igWebForm.handle} onChange={(e) => setIgWebForm((f) => ({ ...f, handle: e.target.value }))} placeholder="Instagram handle (e.g. 3dartix_marketplace)" className="rounded-sm text-xs h-8" data-testid="ig-web-handle-input" />
                            <Textarea value={igWebForm.storage_state} onChange={(e) => setIgWebForm((f) => ({ ...f, storage_state: e.target.value }))} placeholder='Paste Playwright storage_state JSON: {"cookies":[...],"origins":[...]} — never include a password' rows={4} className="rounded-sm text-[10px] font-mono" data-testid="ig-web-storage-state-input" />
                            <p className="text-[10px] text-muted-foreground">Log into Instagram in your own browser, export a Playwright <code>storage_state</code> (cookies only), and paste it here. Validated, encrypted (AES-256-GCM) and never logged.</p>
                            <div className="flex items-center gap-2">
                              <Button size="sm" disabled={igWebBusy || !igWebForm.handle || !igWebForm.storage_state} onClick={importIgWebSession} className="rounded-sm h-7 text-[10px]" data-testid="ig-web-import-button"><UploadSimple size={12} className="mr-1" />Import session</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setIgWebOpen(false); setIgWebForm({ handle: "", storage_state: "" }); }} className="rounded-sm h-7 text-[10px]" data-testid="ig-web-cancel-button">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setIgWebOpen(true)} className="rounded-sm" data-testid="ig-web-configure-button"><Plus size={14} className="mr-1" />Import browser session</Button>
                        )}
                      </div>
                    )}

                    {open && (
                      <div className="space-y-3 rounded-sm bg-muted/20 border border-border/60 p-3" data-testid={`connector-advanced-${c.platform}`}>
                        {!isMeta && (
                          <div className="space-y-2">
                            <div className="overline text-muted-foreground">Credentials</div>
                            <Input value={creds.client_id || ""} onChange={(e) => setConnCreds((s) => ({ ...s, [c.platform]: { ...creds, client_id: e.target.value } }))} className="rounded-sm font-mono text-xs" placeholder="Client ID" data-testid={`${c.platform}-client-id`} />
                            <Input type="password" value={creds.client_secret || ""} onChange={(e) => setConnCreds((s) => ({ ...s, [c.platform]: { ...creds, client_secret: e.target.value } }))} className="rounded-sm font-mono text-xs" placeholder="Client Secret" data-testid={`${c.platform}-client-secret`} />
                            <ReadOnly label="Redirect URI" value={`${API_BASE}/social/oauth/callback`} />
                            <ReadOnly label="OAuth Callback URL" value={`${API_BASE}/social/oauth/callback`} />
                            <p className="text-[10px] text-muted-foreground">Leave empty for demo mode. Paste real credentials, then Connect to go live — no code change.</p>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <div className="overline text-muted-foreground">Advanced (read-only)</div>
                          <ReadOnly label="Authorization Endpoint" value={oauth.authorize_url} />
                          <ReadOnly label="Token Endpoint" value={oauth.token_url} />
                          <ReadOnly label="OAuth Scopes" value={(oauth.scopes || []).join(" ")} />
                          <ReadOnly label="API Base URL" value={PLATFORM_API_BASE[c.platform]} />
                          <div className="grid grid-cols-3 gap-2">
                            <ReadOnly label="Environment" value={prod ? "Production" : "Demo"} />
                            <ReadOnly label="Connector" value={`v${c.version}`} />
                            <ReadOnly label="SDK" value={`v${c.sdk_version || "1.0.0"}`} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
